import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slide,
  TextField,
} from "@material-ui/core";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import { makeStyles } from "@material-ui/core/styles";
import Select from "components/Select/Select";
import { ethers } from "ethers";
import React, { useEffect, useMemo, useState } from "react";
import { paymentCoinService } from "services/paymentCoinService";
import { rewardTierService } from "services/rewardTierService";
import RewardTierEdit from "./RewardTierEdit";
import { tierName } from "./const";
import {
  getCurrentBalance,
  getRewardTokenName,
  getTier,
  getTokenInfo,
  getTotalRewardNeed,
  rewardAddress,
  rewardToken,
  setTokenReward,
} from "./reward";

const useStyles = makeStyles({
  table: {
    minWidth: 650,
  },
});

const totalTier = 3;

export default function TierTable() {
  const classes = useStyles();
  const [tiers, setTiers] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [loadingEdit, setLoadingEdit] = React.useState(false);
  const [currentBalance, setCurrentBalance] = React.useState(0);
  const [rewardTokenName, setRewardTokenName] = React.useState("");
  const [tokenAddressReward, setTokenAddressReward] = React.useState("");
  const [tokenRewardInput, setTokenRewardInput] = React.useState("");
  const [totalNeeded, setTotalNeeded] = React.useState(0);
  const [open, setOpen] = useState(false);

  const paymentCoins = usePaymentCoin();
  const [selectedPaymentCoinAddress, setSelectedPaymentCoinAddress] = React.useState(
    ethers.constants.AddressZero,
  );

  const selectedPaymentCoin = useMemo(() => {
    return paymentCoins.find((coin) => coin.address === selectedPaymentCoinAddress);
  }, [paymentCoins, selectedPaymentCoinAddress]);

  const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="down" ref={ref} {...props} />;
  });

  async function fetchTiers() {
    const tiers = [];
    setLoading(true);
    try {
      await Promise.all(
        Array.from({ length: totalTier }, (_, i) => i).map(async (i) => {
          const response = await getTier(i, selectedPaymentCoinAddress);
          tiers.push(response);
        }),
      );
      tiers.sort((a, b) => a.id - b.id);
      setTiers(tiers);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  async function reloadOneTier(id) {
    setLoading(true);
    try {
      const tier = await getTier(id, selectedPaymentCoinAddress);
      const newTiers = tiers.map((t) => (t.id === id ? tier : t));
      setTiers(newTiers);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  async function fetchBalance() {
    try {
      const balance = await getCurrentBalance();
      const tokenName = await getRewardTokenName();
      const totalNeeded = await getTotalRewardNeed();
      const getRewardToken = await rewardToken();
      setCurrentBalance(balance);
      setRewardTokenName(tokenName);
      setTotalNeeded(totalNeeded);
      setTokenAddressReward(getRewardToken);
    } catch (e) {
      console.error(e);
    }
  }

  const handleUpdate = async () => {
    try {
      if (!tokenRewardInput) return;
      setLoadingEdit(true);
      const tx = await setTokenReward(tokenRewardInput);
      await tx.wait();
      const { symbol, decimal } = await getTokenInfo(tokenRewardInput);
      await rewardTierService.create({
        rewardAddress: tokenRewardInput,
        symbol,
        decimal,
      });
      setTokenRewardInput("");
      setOpen(false);
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingEdit(false);
    }
  };

  useEffect(() => {
    fetchTiers();
    fetchBalance();
  }, [selectedPaymentCoinAddress]);

  return (
    <>
      <p>
        <strong>Reward contract:</strong> {rewardAddress}
      </p>
      <p>
        <strong>Token reward:</strong> {tokenAddressReward}{" "}
        <Button variant="transparent" onClick={() => setOpen(!open)}>
          Edit
        </Button>
      </p>
      <p>
        <strong>Current balance:</strong> {currentBalance} {rewardTokenName}
      </p>
      <p>
        <strong>Total needed:</strong> {totalNeeded} {rewardTokenName}
      </p>

      <div>
        <Select
          label={"Payment Coin"}
          value={selectedPaymentCoinAddress}
          onChange={(value) => setSelectedPaymentCoinAddress(value)}
          options={paymentCoins.map((coin) => ({
            value: coin.address,
            label: coin.name,
          }))}
        />
      </div>

      <Table className={classes.table} aria-label="tier table">
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>Name</TableCell>
            <TableCell align="right">Seller Reward per 1 {selectedPaymentCoin?.name}</TableCell>
            <TableCell align="right">Buyer Reward per 1 {selectedPaymentCoin?.name}</TableCell>
            <TableCell align="right">
              Collection Owner Reward per 1 {selectedPaymentCoin?.name}
            </TableCell>
            <TableCell align="right">Edit</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading && (
            <TableRow>
              <TableCell>Loading...</TableCell>
            </TableRow>
          )}
          {tiers.map((tier) => (
            <TableRow key={tier.id}>
              <TableCell component="th" scope="row">
                {tier.id}
              </TableCell>
              <TableCell align="right">{tierName[tier.id]}</TableCell>
              <TableCell align="right">{tier.seller}</TableCell>
              <TableCell align="right">{tier.buyer}</TableCell>
              <TableCell align="right">{tier.collectionOwner}</TableCell>
              <TableCell align="right">
                <RewardTierEdit
                  paymentCoin={selectedPaymentCoin}
                  tier={tier}
                  reload={() => reloadOneTier(tier.id)}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog
        classes={{
          root: classes.center,
          paper: classes.modal,
        }}
        open={open}
        transition={Transition}
        keepMounted
        onClose={() => setOpen(!open)}
      >
        <DialogTitle>Edit Token Reward</DialogTitle>
        <DialogContent id="modal-slide-description" className={classes.modalBody}>
          <TextField
            label="Address"
            variant="standard"
            style={{ width: 300 }}
            onChange={(e) => setTokenRewardInput(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => (loading ? handleUpdate() : {})}>
            {loadingEdit ? <CircularProgress size={20} /> : "Update"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export function usePaymentCoin() {
  const [paymentCoins, setPaymentCoins] = React.useState([]);

  async function fetchPaymentCoins() {
    try {
      const { data } = await paymentCoinService.getAll({
        page: 0,
        size: 10000,
      });
      setPaymentCoins(data.items);
    } catch (e) {
      console.log(e);
    }
  }

  useEffect(() => {
    fetchPaymentCoins();
  }, []);

  return paymentCoins;
}
